import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Step1ProjectType, Step1Data } from '@/components/wizard/Step1ProjectType';
import { Step2Description, Step2Data } from '@/components/wizard/Step2Description';
import { Step3Features, Step3Data } from '@/components/wizard/Step3Features';
import { Step4Design, Step4Data } from '@/components/wizard/Step4Design';
import { Step5UsersScale, Step5Data } from '@/components/wizard/Step5UsersScale';
import { Step6Integrations, Step6Data } from '@/components/wizard/Step6Integrations';
import { Step7TimelineBudget, Step7Data } from '@/components/wizard/Step7TimelineBudget';
import { Step8Review } from '@/components/wizard/Step8Review';

interface WizardData {
  step1?: Step1Data;
  step2?: Step2Data;
  step3?: Step3Data;
  step4?: Step4Data;
  step5?: Step5Data;
  step6?: Step6Data;
  step7?: Step7Data;
}

export const Wizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardData>({});
  const [quoteEstimate, setQuoteEstimate] = useState<{ min: number; max: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);

  useEffect(() => {
    // Create initial project when wizard starts
    const createProject = async () => {
      try {
        const response = await fetch('/api/projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: 'New Project', status: 'draft' }),
        });

        if (response.ok) {
          const data = await response.json();
          setProjectId(data.data.id);

          // Create wizard session
          const sessionResponse = await fetch('/api/wizard/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ project_id: data.data.id }),
          });

          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();
            setSessionId(sessionData.data.id);
          }
        }
      } catch (error) {
        console.error('Failed to create project:', error);
      }
    };

    createProject();
  }, []);

  const saveStepData = async (step: number, data: unknown) => {
    if (!sessionId) return;

    try {
      await fetch(`/api/wizard/sessions/${sessionId}/step/${step}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          response: data,
          step_name: `step_${step}`,
        }),
      });
    } catch (error) {
      console.error('Failed to save step data:', error);
    }
  };

  const calculateEstimate = (data: WizardData) => {
    // Base prices by project type
    const basePrices: Record<string, number> = {
      web_app: 25000,
      mobile_app: 50000,
      api_backend: 20000,
      ecommerce: 40000,
      dashboard: 30000,
      ai_ml: 60000,
      other: 30000,
    };

    let basePrice = basePrices[data.step1?.projectType || 'other'] || 30000;

    // Feature multipliers
    const featureCount = data.step3?.features?.length || 0;
    const featureMultiplier = 1 + (featureCount * 0.1);

    // Scale multiplier
    const scaleMultipliers: Record<string, number> = {
      '1-100': 1,
      '100-1k': 1.2,
      '1k-10k': 1.5,
      '10k+': 2,
    };
    const scaleMultiplier = scaleMultipliers[data.step5?.expectedUsers || '1-100'] || 1;

    // Timeline urgency multiplier
    const timelineMultipliers: Record<string, number> = {
      asap: 1.5,
      '1-2weeks': 1.3,
      '1month': 1,
      '2-3months': 0.9,
      flexible: 0.8,
    };
    const timelineMultiplier = timelineMultipliers[data.step7?.timeline || '1month'] || 1;

    // Integration multiplier
    const integrationCount = data.step6?.integrations?.length || 0;
    const integrationMultiplier = 1 + (integrationCount * 0.15);

    const estimatedPrice = basePrice * featureMultiplier * scaleMultiplier * timelineMultiplier * integrationMultiplier;

    return {
      min: Math.round(estimatedPrice * 0.8),
      max: Math.round(estimatedPrice * 1.2),
    };
  };

  const handleStep1Next = (data: Step1Data) => {
    setWizardData({ ...wizardData, step1: data });
    saveStepData(1, data);
    setCurrentStep(2);
  };

  const handleStep2Next = (data: Step2Data) => {
    setWizardData({ ...wizardData, step2: data });
    saveStepData(2, data);
    setCurrentStep(3);
  };

  const handleStep3Next = (data: Step3Data) => {
    setWizardData({ ...wizardData, step3: data });
    saveStepData(3, data);
    setCurrentStep(4);
  };

  const handleStep4Next = (data: Step4Data) => {
    setWizardData({ ...wizardData, step4: data });
    saveStepData(4, data);
    setCurrentStep(5);
  };

  const handleStep5Next = (data: Step5Data) => {
    setWizardData({ ...wizardData, step5: data });
    saveStepData(5, data);
    setCurrentStep(6);
  };

  const handleStep6Next = (data: Step6Data) => {
    setWizardData({ ...wizardData, step6: data });
    saveStepData(6, data);
    setCurrentStep(7);
  };

  const handleStep7Next = (data: Step7Data) => {
    const updatedData = { ...wizardData, step7: data };
    setWizardData(updatedData);
    saveStepData(7, data);

    // Calculate estimate
    const estimate = calculateEstimate(updatedData);
    setQuoteEstimate(estimate);

    setCurrentStep(8);
  };

  const handleSubmit = async () => {
    if (!sessionId || !projectId) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/wizard/sessions/${sessionId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          wizard_data: wizardData,
          quote_estimate: quoteEstimate,
        }),
      });

      if (response.ok) {
        // Redirect to projects page
        navigate('/projects');
      } else {
        alert('Failed to submit project. Please try again.');
      }
    } catch (error) {
      console.error('Failed to submit:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const stepTitles = [
    'Project Type',
    'Description',
    'Features',
    'Design',
    'Users & Scale',
    'Integrations',
    'Timeline & Budget',
    'Review',
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {stepTitles.map((_title, index) => (
              <div key={index} className="flex-1 flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    index + 1 < currentStep
                      ? 'bg-green-600 text-white'
                      : index + 1 === currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {index + 1 < currentStep ? '✓' : index + 1}
                </div>
                {index < stepTitles.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index + 1 < currentStep ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-600">
            Step {currentStep} of 8: {stepTitles[currentStep - 1]}
          </p>
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="pt-6">
            {currentStep === 1 && (
              <Step1ProjectType onNext={handleStep1Next} initialData={wizardData.step1} />
            )}
            {currentStep === 2 && (
              <Step2Description
                onNext={handleStep2Next}
                onBack={handleBack}
                initialData={wizardData.step2}
              />
            )}
            {currentStep === 3 && (
              <Step3Features
                onNext={handleStep3Next}
                onBack={handleBack}
                initialData={wizardData.step3}
              />
            )}
            {currentStep === 4 && (
              <Step4Design
                onNext={handleStep4Next}
                onBack={handleBack}
                initialData={wizardData.step4}
              />
            )}
            {currentStep === 5 && (
              <Step5UsersScale
                onNext={handleStep5Next}
                onBack={handleBack}
                initialData={wizardData.step5}
              />
            )}
            {currentStep === 6 && (
              <Step6Integrations
                onNext={handleStep6Next}
                onBack={handleBack}
                initialData={wizardData.step6}
              />
            )}
            {currentStep === 7 && (
              <Step7TimelineBudget
                onNext={handleStep7Next}
                onBack={handleBack}
                initialData={wizardData.step7}
              />
            )}
            {currentStep === 8 && (
              <Step8Review
                onSubmit={handleSubmit}
                onBack={handleBack}
                wizardData={wizardData}
                quoteEstimate={quoteEstimate || undefined}
                isSubmitting={isSubmitting}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
